import { useCallback, useMemo } from "react";

import { useIsNodeStaff } from "@js/apps/common/hooks/is-node-staff";
import { shareApplicantBulkClicked } from "@js/apps/employer/actions";
import { openShareBidsWithTeamMembersModal } from "@js/apps/employer/components/share-bids-with-team-members-modal";
import { useCanShareWithTeamMembers } from "@js/apps/employer/hooks/use-can-share-with-team-members";
import { useBidsContext } from "@js/apps/jobs/context/refetch-bids-context";
import { useHandleSendAIInterviewRequest } from "@js/apps/jobs/hooks/use-handle-send-ai-interview-request";
import { openAddToRosterModal } from "@js/apps/rosters/components/add-to-roster-modal";
import { useAppDispatch, useAppSelector } from "@js/hooks";
import type { EmployerBidListBid, Job } from "@js/types/jobs";

import { useHideProposalsMutation } from "../../api";
import { hideApplications } from "../../components/bid-actions-menu/hide-applications";
import {
  openInterviewRequestMessage,
  openRejectOfferMessageModal,
} from "../../components/bid-messages-modals";
import {
  getIsAnyOfBidsUnapproved,
  getSelectedBidIds,
  resetSelectedBids,
} from "../../components/bulk-select-actions/bid-bulk-selection-slice";
import { openBidsBulkSendSmsModal } from "../../components/bulk-select-actions/bids-bulk-send-sms-modal";
import { useBulkSendApplicationData } from "../use-bulk-send-application-data";
import { useSelectedBidsIds } from "../use-selected-bids-ids";
import { useSelectedInterviewableBids } from "../use-selected-interviewable-bids";
import { useSelectedRejectableBids } from "../use-selected-rejectable-bids";

export const useBulkSelectActions = (
  job: Job,
  bidList: EmployerBidListBid[],
) => {
  const [hideProposals] = useHideProposalsMutation();
  const isNodeStaff = useIsNodeStaff();
  const {
    canSendApplicationData,
    handleSendSelectedApplications,
    isSendingApplicationsBlocked,
    isLoading: isSendingApplicationsData,
  } = useBulkSendApplicationData({ job, bidList });
  const selectedBidIds = useAppSelector(getSelectedBidIds);
  const selectedBidsTotal = selectedBidIds.length;
  const { selectedBidsIds } = useSelectedBidsIds();
  const { refetchBidList } = useBidsContext();
  const dispatch = useAppDispatch();
  const { selectedInterviewableBids } = useSelectedInterviewableBids(bidList);
  const isAnyOfSelectedInterviewableBidsUnapproved = getIsAnyOfBidsUnapproved(
    selectedInterviewableBids,
  );
  const selectedInterviewableIds = useMemo(
    () => selectedInterviewableBids.map((bid) => bid.id),
    [selectedInterviewableBids],
  );
  const { selectedRejectableBids } = useSelectedRejectableBids(bidList);
  const { handleSendAIInterviewRequest } = useHandleSendAIInterviewRequest();
  const canSendRejection = selectedRejectableBids.length > 0;
  const canSendInterviewRequest =
    selectedInterviewableIds.length > 0 && job.is_open;

  const handleHideProposals = async (bidIds: number[], isHidden: boolean) => {
    await hideProposals({ bidIds, isHidden });
  };

  const areAllSelectedBidsHidden = useMemo(() => {
    return (
      bidList.filter((el) => selectedBidsIds.includes(el.id) && el.is_hidden)
        .length === selectedBidsIds.length
    );
  }, [bidList, selectedBidsIds]);

  const bulkHideBids = useCallback(
    (isShowingHiddenBids: boolean) => {
      void hideApplications(
        selectedBidsIds,
        handleHideProposals,
        isShowingHiddenBids,
      );
      dispatch(resetSelectedBids());
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selectedBidsIds, handleHideProposals],
  );

  const bulkSendInterviewRequest = useCallback(() => {
    openInterviewRequestMessage({
      job,
      refetchBidList,
      bids: selectedInterviewableBids,
    });
  }, [job, refetchBidList, selectedInterviewableBids]);

  const bulkSendAIInterviewRequest = useCallback(async () => {
    void handleSendAIInterviewRequest({
      bids: selectedInterviewableIds,
      refetchBidList,
      job,
    });
  }, [
    refetchBidList,
    selectedInterviewableIds,
    job,
    handleSendAIInterviewRequest,
  ]);

  const bulkRejectOffer = useCallback(() => {
    const nextUrl =
      selectedRejectableBids.length === 1
        ? `/jobs/${job.id}/proposals/${selectedRejectableBids[0].id}/rejection_feedback`
        : undefined;

    openRejectOfferMessageModal({
      refetchBidList,
      bids: selectedRejectableBids,
      nextUrl,
      job,
    });
  }, [selectedRejectableBids, refetchBidList, job]);

  const canShareBidsWithTeamMembers = useCanShareWithTeamMembers();

  const handleShareBidsClick = () => {
    dispatch(shareApplicantBulkClicked());
    openShareBidsWithTeamMembersModal({
      jobOwnerId: job.creator?.id,
      bidIds: selectedBidsIds,
    });
  };

  const bulkSendSms = useCallback(() => {
    const selectedBids = bidList.filter((bid) =>
      selectedBidsIds.includes(bid.id),
    );
    openBidsBulkSendSmsModal({
      selectedBids,
      jobUrl: `${window.location.origin}/jobs/${job.id}/`,
    });
  }, [bidList, selectedBidsIds, job.id]);

  const bulkAddToRoster = useCallback(() => {
    const freelancerIds = bidList
      .filter((bid) => selectedBidsIds.includes(bid.id))
      .map((bid) => bid.freelancer.id);

    openAddToRosterModal({
      freelancerIds,
      onSuccess: () => dispatch(resetSelectedBids()),
    });
  }, [bidList, selectedBidsIds, dispatch]);

  return {
    bulkHideBids,
    bulkSendInterviewRequest,
    bulkSendAIInterviewRequest,
    bulkRejectOffer,
    bulkSendSms,
    bulkAddToRoster,
    canSendSms: isNodeStaff && selectedBidsTotal > 0,
    canAddToRoster: isNodeStaff && selectedBidsTotal > 0,
    handleShareBidsClick,
    canShareBidsWithTeamMembers,
    selectedBidsTotal,
    selectedInterviewableBidsTotal: selectedInterviewableBids.length,
    canSendInterviewRequest,
    canSendRejection,
    isAnyOfSelectedInterviewableBidsUnapproved,
    areAllSelectedBidsHidden,
    handleSendSelectedApplications,
    canSendApplicationData,
    isSendingApplicationsData,
    isSendingApplicationsBlocked,
  };
};
